import {ListViewFilterJSON,
        ListViewFilterModel,
        ListViewFilterFactory} from "./models";
import {SortOrderJSON,
        SortOrderModel,
        SortOrderFactory} from "./modelsUserConfiguration";

export interface PaginatorJSON {
  current_page: number;
  next_page: number;
  previous_page: number;
  record_count: number;
  page_size: number;
}

export class PaginatorModel {
  currentPage: number;
  nextPage: number;
  previousPage: number;
  recordCount: number;
  pageSize: number;
  loadedPages: number[];
  loadingPages: number[];

  constructor(
    currentPage: number,
    nextPage: number,
    previousPage: number,
    recordCount: number,
    pageSize: number,
    loadedPages: number[],
    loadingPages: number[]
  ) {

    this.currentPage = currentPage;
    this.nextPage = nextPage;
    this.previousPage = previousPage;
    this.recordCount = recordCount;
    this.pageSize = pageSize;
    this.loadedPages = loadedPages;
    this.loadingPages = loadingPages;
  }
}

export class PaginatorFactory {
  /**
   *
   * @param json
   */
  fromJSON(json: PaginatorJSON | null): PaginatorModel {
    if(json == null){
      return this.init()
    }

    return new PaginatorModel(
      json.current_page,
      json.next_page,
      json.previous_page,
      json.record_count,
      json.page_size,
      [],
      []
    );
  }
  /**
   *
   * @param model
   */
  toJSON(model: PaginatorModel): PaginatorJSON {
    return {
      current_page: model.currentPage,
      next_page: model.nextPage,
      previous_page: model.previousPage,
      record_count: model.recordCount,
      page_size: model.pageSize
    };
  }

  /***/
  init(): PaginatorModel {

    return new PaginatorModel(
      0,
      0,
      0,
      0,
      200,
      [],
      []
    );
  }

}

//************************************************//
//************************************************//
//************************************************//


export interface SearchTermJSON {
  search_text: string;
  backend_search_performed: boolean;
}

export class SearchTermModel {
  searchText: string;
  backendSearchPerformed: boolean

  constructor (
    searchText: string,
    backendSearchPerformed: boolean
  ) {
    this.searchText = searchText;
    this.backendSearchPerformed = backendSearchPerformed;
  }
}

export class SearchTermFactory {

  fromJSON(json: SearchTermJSON): SearchTermModel {
    return new SearchTermModel(json.search_text, json.backend_search_performed)
  }

  toJSON(model: SearchTermModel): SearchTermJSON {
    return {
      search_text: model.searchText,
      backend_search_performed: model.backendSearchPerformed
    }
  }

  init(): SearchTermModel {
    return new SearchTermModel('', false)
  }

}

//************************************************//
//************************************************//
//************************************************//

export type ListDisplayTypeType = 'List' | 'BarChart'

export interface ListConfigurationJSON {
  listview_uuid: string;
  listview_label: string;
  display_type: ListDisplayTypeType;
  apply_custom_filters: boolean;
  filters: ListViewFilterJSON[];
  filter_logic: string;
}

export class ListConfigurationModel {
  listViewUuid: string;
  listViewLabel: string;
  displayType: ListDisplayTypeType;
  applyCustomFilters: boolean;
  filters: ListViewFilterModel[];
  filterLogic: string;

  constructor (
    listViewUuid: string,
    listViewLabel: string,
    displayType: ListDisplayTypeType,
    applyCustomFilters: boolean,
    filters: ListViewFilterModel[],
    filterLogic: string,
  ) {
    this.listViewUuid = listViewUuid;
    this.listViewLabel = listViewLabel;
    this.displayType = displayType;
    this.applyCustomFilters = applyCustomFilters;
    this.filters = filters;
    this.filterLogic = filterLogic;
  }
}

export class ListConfigurationFactory {

  fromJSON(json: ListConfigurationJSON): ListConfigurationModel {
    const filterFactory = new ListViewFilterFactory()
    const thisFilters = json.filters.map((thisFilter: ListViewFilterJSON) => filterFactory.fromJSON(thisFilter));
    return new ListConfigurationModel(
      json.listview_uuid,
      json.listview_label,
      json.display_type,
      json.apply_custom_filters,
      thisFilters,
      json.filter_logic
    )
  }

  toJSON(model: ListConfigurationModel): ListConfigurationJSON {
    const filterFactory = new ListViewFilterFactory()
    const thisFilters = model.filters.map((thisFilter: ListViewFilterModel) => filterFactory.toJSON(thisFilter))
    return {
      listview_uuid: model.listViewUuid,
      listview_label: model.listViewLabel,
      display_type: model.displayType,
      apply_custom_filters: model.applyCustomFilters,
      filters: thisFilters,
      filter_logic: model.filterLogic
    }
  }

  init(): ListConfigurationModel {
    const thisUuid = '';
    const thisLabel = '';
    const thisDisplayType = 'List';
    const thisApplyCustomFilters = false;
    const thisFilters = [] as ListViewFilterModel[];
    const thisFilterLogic = '';

    return new ListConfigurationModel(thisUuid, thisLabel, thisDisplayType, thisApplyCustomFilters, thisFilters, thisFilterLogic)
  }

}

//************************************************//
//************************************************//
//************************************************//

export interface ListViewConfigurationJSON {
  search_term: SearchTermJSON;
  paginator: PaginatorJSON;
}

export class ListViewConfigurationModel {
  searchTerm: SearchTermModel;
  paginator: PaginatorModel;

  constructor (
    searchTerm: SearchTermModel,
    paginator: PaginatorModel,
  ) {
    this.searchTerm = searchTerm;
    this.paginator = paginator;
  }
}

export class ListViewConfigurationFactory {

  fromJSON(json: ListViewConfigurationJSON): ListViewConfigurationModel {
    const thisSearchTerm = new SearchTermFactory().fromJSON(json.search_term);
    const thisPaginator = new PaginatorFactory().fromJSON(json.paginator);
    return new ListViewConfigurationModel(
      thisSearchTerm,
      thisPaginator
    )
  }

  toJSON(model: ListViewConfigurationModel): ListViewConfigurationJSON {
    const thisSearchTerm = new SearchTermFactory().toJSON(model.searchTerm);
    const thisPaginator = new PaginatorFactory().toJSON(model.paginator);
    return {
      search_term: thisSearchTerm,
      paginator: thisPaginator,
    }
  }

  init(): ListViewConfigurationModel {
    const thisSearchTerm = new SearchTermFactory().init();
    const thisPaginator = new PaginatorFactory().init();

    return new ListViewConfigurationModel(thisSearchTerm, thisPaginator)
  }

}

//************************************************//
//************************************************//
//************************************************//

export interface RelatedListConfigurationJSON {
  paginator: PaginatorJSON,
  search_term: SearchTermJSON,
  sort_orders: SortOrderJSON[]
}

export class RelatedListConfigurationModel {
  searchTerm: SearchTermModel;
  paginator: PaginatorModel;
  sortOrders: SortOrderModel[];

  constructor (
    searchTerm: SearchTermModel,
    paginator: PaginatorModel,
    sortOrders: SortOrderModel[]
  ) {
    this.searchTerm = searchTerm;
    this.paginator = paginator;
    this.sortOrders = sortOrders;
  }
}

export class RelatedListConfigurationFactory {

  fromJSON(json: RelatedListConfigurationJSON): RelatedListConfigurationModel {
    const thisSearchTerm = new SearchTermFactory().fromJSON(json.search_term);
    const thisPaginator = new PaginatorFactory().fromJSON(json.paginator);
    const sortFactory = new SortOrderFactory()
    const thisSortOrders = json.sort_orders.map((thisSortOrder: SortOrderJSON) => sortFactory.fromJSON(thisSortOrder));
    return new RelatedListConfigurationModel(
      thisSearchTerm,
      thisPaginator,
      thisSortOrders
    )
  }

  toJSON(model: RelatedListConfigurationModel): RelatedListConfigurationJSON {
    const thisSearchTerm = new SearchTermFactory().toJSON(model.searchTerm);
    const thisPaginator = new PaginatorFactory().toJSON(model.paginator);
    const sortFactory = new SortOrderFactory()
    const thisSortOrders = model.sortOrders.map((thisSortOrder: SortOrderModel) => sortFactory.toJSON(thisSortOrder));
    return {
      search_term: thisSearchTerm,
      paginator: thisPaginator,
      sort_orders: thisSortOrders
    }
  }

  init(): RelatedListConfigurationModel {
    const thisSearchTerm = new SearchTermFactory().init();
    const thisPaginator = new PaginatorFactory().init();
    const thisSortOrders = [] as SortOrderModel[];

    return new RelatedListConfigurationModel(thisSearchTerm, thisPaginator, thisSortOrders)
  }

}
